import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ActivityWindowSelector from './ActivityWindowSelector';
import { getLocale } from '../DateRangeSelector';
import BrandList from './BrandList';
import DataBuildingBanner from './DataBuildingBanner';
import EmptyState from './EmptyState';
import FilterFields from './FilterFields';
import ListToolbar from './ListToolbar';
import NotCarriedTable from './NotCarriedTable';
import ViewChips from './ViewChips';
import useBuyerInsightsFilters from './useBuyerInsightsFilters';
import useContentWidth, { isWideWidth } from './useContentWidth';
import { BRAND_VIEWS, NOT_CARRIED_NOTE } from './constants';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import {
  BRAND_VIEW_DESCRIPTIONS,
  brandEmptyText,
  buildBrandsParams,
  cohortLine,
  filterParams,
  fmtInt,
} from '../../../utils/buyerInsightsFormat';

/**
 * Brands tab (spec §1, stage 4 part A). Every carried-brand number is the Products scope grouped
 * by brand, so links into Products land on the same counts. `onOpenProducts(initial)` opens the
 * Products tab with the view, group and filters preset.
 */
const BrandsTab = ({ myRetailers = [], myRetailerId = '', onRetailerChange, onOpenProducts, initial = {}, onFiltersChange }) => {
  const rootRef = useRef(null);
  const width = useContentWidth(rootRef);
  const isWide = isWideWidth(width);

  const f = useBuyerInsightsFilters({ tab: 'brands', myRetailerId, initial, onFiltersChange });
  // No search box on Brands: the Brand filter is searchable (Daniel 2026-10-05); debouncedQ stays ''.
  const { meta, window, setWindow, brand, category, status, live, sale, soldOutAt, offRange, bands, debouncedQ, hasFilters } = f;
  const [view, setView] = useState('all');
  const [sort, setSort] = useState('sent_clicks'); // as on Products
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [expandAll, setExpandAll] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [depth, setDepth] = useState(null); // { key, counts, brands } — the second request
  const reqSeq = useRef(0);
  const abortRef = useRef(null);
  const depthKeyRef = useRef('');
  const notCarried = view === 'not_carried';

  const params = useMemo(
    () => buildBrandsParams({ window, myRetailerId, view, brand, category, status, live, sale, soldOutAt, offRange, bands, q: debouncedQ, sort }),
    [window, myRetailerId, view, brand, category, status, live, sale, soldOutAt, offRange, bands, debouncedQ, sort]
  );
  const paramsKey = JSON.stringify(params);
  // The depth pass depends on the filters only (not view, sort or page): one request per filter set.
  const detailParams = useMemo(
    () => filterParams({ window, myRetailerId, brand, category, status, live, sale, soldOutAt, offRange, bands, q: debouncedQ }),
    [window, myRetailerId, brand, category, status, live, sale, soldOutAt, offRange, bands, debouncedQ]
  );
  const depthKey = JSON.stringify(detailParams);

  // Second step: after the list has loaded (so the shared scope is cached server-side), fetch the
  // Thinner counts. Re-fetched only when the filters change.
  const loadDepth = useCallback((key, p) => {
    if (depthKeyRef.current === key) return;
    depthKeyRef.current = key;
    setDepth((prev) => (prev && prev.key === key ? prev : null));
    dashboardAPI
      .getBuyerInsightsBrandsDepth(p)
      .then((res) => {
        if (depthKeyRef.current === key && res?.success) setDepth({ key, ...res.data });
      })
      .catch(() => {
        if (depthKeyRef.current === key) depthKeyRef.current = ''; // retried on the next list load
      });
  }, []);

  // Page 1 on every params change; stale responses are discarded.
  useEffect(() => {
    reqSeq.current += 1;
    const mine = reqSeq.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = controller;
    setLoading(true);
    setError('');
    dashboardAPI
      .getBuyerInsightsBrands({ ...params, page: 1 }, { signal: controller?.signal })
      .then((res) => {
        if (mine !== reqSeq.current) return;
        if (res?.success) {
          setData(res.data);
          setExpandAll(false);
          setExpanded(new Set());
          loadDepth(depthKey, detailParams);
        } else {
          setError(res?.error || 'Could not load brands');
        }
      })
      .catch((e) => {
        if (mine !== reqSeq.current || e?.name === 'AbortError') return;
        setError(e.message || 'Could not load brands');
      })
      .finally(() => {
        if (mine === reqSeq.current) setLoading(false);
      });
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rows of the previous view are not shown under the new view's columns. Thinner signals come
  // from the depth request unless the row already carries them (the Thinner views).
  const depthReady = depth && depth.key === depthKey;
  const rows = useMemo(() => {
    const base = data && data.view === view ? data.rows : [];
    if (!depthReady) return base;
    return base.map((r) => (r.thinner ? r : { ...r, thinner: depth.brands[r.brand] || { sizes: 0, colours: 0 } }));
  }, [data, view, depth, depthReady]);
  const counts = data ? { ...(depthReady ? depth.counts : {}), ...data.counts } : null;
  const total = data?.total || 0;
  const hasMore = Boolean(data) && rows.length < total;

  const showMore = useCallback(async () => {
    if (!data || loadingMore) return;
    const mine = reqSeq.current;
    setLoadingMore(true);
    try {
      const res = await dashboardAPI.getBuyerInsightsBrands({ ...params, page: data.page + 1 });
      if (mine !== reqSeq.current || !res?.success) return;
      setData((prev) => ({ ...res.data, rows: [...prev.rows, ...res.data.rows] }));
    } catch (e) {
      setError(e.message || 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  }, [data, loadingMore, params]);

  const isExpanded = (b) => expandAll || expanded.has(b);
  const allExpanded = expandAll || (rows.length > 0 && rows.every((r) => expanded.has(r.brand)));
  const onToggle = (b) => {
    if (expandAll) {
      setExpandAll(false);
      setExpanded(new Set());
      return;
    }
    const opening = !expanded.has(b);
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(b)) s.delete(b);
      else s.add(b);
      return s;
    });
    if (opening) track('Buyer Insights Row Expanded', { tab: 'brands', view, all: false });
  };
  const onToggleAll = () => {
    if (allExpanded) {
      setExpandAll(false);
      setExpanded(new Set());
    } else {
      setExpandAll(true);
      setExpanded(new Set());
      track('Buyer Insights Row Expanded', { tab: 'brands', view, all: true });
    }
  };

  const onSelectView = (key, count) => {
    setView(key);
    track('Buyer Insights View Selected', { tab: 'brands', view: key, count, source: 'chip' });
  };
  const onSort = (column) => {
    setSort(column);
    track('Buyer Insights Sorted', { tab: 'brands', view, column, layout: isWide ? 'wide' : 'narrow' });
  };
  // A link into Products carries this tab's filters and window (spec §5 stage 5).
  const onOpen = (initial, source) => {
    track('Buyer Insights Link Opened', { tab: 'brands', target: initial.view, group: initial.group, source });
    if (onOpenProducts) onOpenProducts({ window, category, status, live, sale, soldOutAt, offRange, bands, ...initial });
  };

  const emptyState = (
    <EmptyState
      tab="brands"
      view={view}
      q={debouncedQ}
      hasFilters={hasFilters}
      signature={paramsKey}
      text={brandEmptyText(view, { q: debouncedQ, hasFilters, category: category.length > 0, sale, soldOutAt })}
    />
  );
  const listProps = { rows, isWide, loading, loadingMore, hasMore, onShowMore: showMore, emptyState };
  const cohort = cohortLine(data?.cohort);

  return (
    <Box ref={rootRef} sx={{ minWidth: 0 }}>
      <DataBuildingBanner firstActivatedAt={meta?.first_activated_at} />

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocale()}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ActivityWindowSelector value={window} onChange={f.onWindowChange} bounds={f.bounds} isWide={isWide} />
            <FilterFields
              f={f}
              isWide={isWide}
              myRetailers={myRetailers}
              myRetailerId={myRetailerId}
              onRetailerChange={onRetailerChange}
              disabledNote={notCarried ? NOT_CARRIED_NOTE : ''}
              showStatus={false}
            />
            {cohort && (
              <Typography variant="body2" color="text.secondary">
                {cohort}
              </Typography>
            )}

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                View
              </Typography>
              <ViewChips views={BRAND_VIEWS} counts={counts} active={view} onSelect={onSelectView} isWide={isWide} loading={loading} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {BRAND_VIEW_DESCRIPTIONS[view]}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {data ? `${fmtInt(total)} ${total === 1 ? 'brand' : 'brands'}` : <Skeleton width={90} />}
              </Typography>
            </Box>
          </Box>
        </LocalizationProvider>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setWindow((w) => ({ ...w }))}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <ListToolbar
        side="both"
        isWide={isWide}
        sort={sort}
        onSort={onSort}
        options={notCarried ? [] : undefined}
        allExpanded={allExpanded}
        onToggleAll={onToggleAll}
        expandDisabled={rows.length === 0}
        hideExpand={notCarried}
        exportProps={{ params, view, tab: 'brands', disabled: loading || total === 0 }}
      />

      {notCarried ? (
        <NotCarriedTable {...listProps} />
      ) : (
        <BrandList
          {...listProps}
          sort={sort}
          onSort={onSort}
          isExpanded={isExpanded}
          onToggle={onToggle}
          onOpen={onOpen}
          detailParams={detailParams}
          window={window}
        />
      )}
    </Box>
  );
};

export default BrandsTab;
